import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Edit, Save, RotateCcw, BookmarkPlus, Bell } from "lucide-react";

const RouteOptions = ({
  start,
  setStart,
  end,
  setEnd,
  startInputRef,
  endInputRef,
  startAutocompleteRef,
  endAutocompleteRef,
  enableRouteCustomization,
  disableRouteCustomization,
  resetCustomization,
  handleEnrollInAlerts,
  routes,
  loading,
  error,
  enrollStatus,
  isDraggable,
  hasRoute,
  enrollLabel,
  enrollDisabled,
  editingRouteId,
  onConfigureAlerts,
}) => {
  return (
    <>
      <div className="space-y-2">
        <div className="space-y-1">
          <label htmlFor="start" className="text-xs font-medium">
            A - Start Location
          </label>
          <div className="relative">
            <Input
              id="start"
              ref={startInputRef}
              value={start}
              onChange={(e) => setStart(e.target.value)}
              placeholder="Enter start location"
              className="w-full"
              disabled={isDraggable}
            />
            <div
              ref={startAutocompleteRef}
              className="absolute top-0 left-0 w-full"
            ></div>
          </div>
        </div>

        <div className="space-y-1">
          <label htmlFor="end" className="text-xs font-medium">
            B - Destination
          </label>
          <div className="relative">
            <Input
              id="end"
              ref={endInputRef}
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              placeholder="Enter destination"
              className="w-full"
              disabled={isDraggable}
            />
            <div
              ref={endAutocompleteRef}
              className="absolute top-0 left-0 w-full"
            ></div>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-1.5 mt-2">
        {isDraggable && (
          <>
            <div className="w-full px-2 py-1.5 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded text-xs leading-relaxed">
              <p className="font-medium">Customization Mode</p>
              <p>• Click route to add waypoints</p>
              <p>• Drag waypoints to reposition</p>
              <p>• Click/right-click waypoints to remove</p>
            </div>

            <div className="flex gap-1.5 w-full">
              <Button
                onClick={disableRouteCustomization}
                variant="outline"
                size="sm"
                className="flex-1"
              >
                <Save className="mr-1 h-3.5 w-3.5" />
                Done
              </Button>
              <Button
                onClick={resetCustomization}
                variant="secondary"
                size="sm"
                className="flex-1"
              >
                <RotateCcw className="mr-1 h-3.5 w-3.5" />
                Reset
              </Button>
            </div>
          </>
        )}

        {editingRouteId && (
          <Button
            onClick={onConfigureAlerts}
            variant="secondary"
            size="sm"
            className="w-full"
          >
            <Bell className="mr-1.5 h-3.5 w-3.5" />
            Alert Settings
          </Button>
        )}

        <div className="flex gap-1.5 w-full">
          {!isDraggable && (
            <Button
              onClick={enableRouteCustomization}
              variant="secondary"
              size="sm"
              disabled={!hasRoute}
            >
              <Edit className="mr-1 h-3.5 w-3.5" />
              Customize
            </Button>
          )}
          <Button
            onClick={handleEnrollInAlerts}
            variant={isDraggable ? "outline" : "secondary"}
            size="sm"
            className="flex-1"
            disabled={!hasRoute || loading || enrollDisabled}
          >
            <BookmarkPlus className="mr-1.5 h-3.5 w-3.5" />
            {enrollLabel || "Save Route"}
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive" className="mt-2">
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {enrollStatus && (
        <Alert className="mt-2">
          <AlertTitle>Alert Status</AlertTitle>
          <AlertDescription>{enrollStatus}</AlertDescription>
        </Alert>
      )}
    </>
  );
};

export default RouteOptions;
